/**
 * LayerToggleControl
 *
 * Leaflet control that flips a map between the street and satellite base layers.
 * Renders as a standard leaflet-bar button (SAT / MAP) so it sits with the zoom
 * controls. Shared by the orchard farm map and the ESPNow irrigation live map so
 * both behave identically.
 */

import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { TileLayerKey } from '../../utils/leafletGeo';

export default function LayerToggleControl({
  mapLayer,
  onToggle,
  position = 'topleft',
}: {
  mapLayer: TileLayerKey;
  onToggle: () => void;
  position?: L.ControlPosition;
}) {
  const map = useMap();

  useEffect(() => {
    const ToggleControl = L.Control.extend({
      onAdd() {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('a', '', container);
        button.href = '#';
        button.title = mapLayer === 'street' ? 'Switch to Satellite' : 'Switch to Street Map';
        button.innerHTML = mapLayer === 'street' ? 'SAT' : 'MAP';
        button.style.cssText =
          'display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;width:34px;height:34px;background:white;color:#333;text-decoration:none;cursor:pointer;';
        L.DomEvent.on(button, 'click', (e) => {
          L.DomEvent.preventDefault(e);
          L.DomEvent.stopPropagation(e);
          onToggle();
        });
        return container;
      },
    });

    const instance = new ToggleControl({ position });
    instance.addTo(map);

    return () => {
      instance.remove();
    };
  }, [map, mapLayer, onToggle, position]);

  return null;
}
